import { hookstate } from '@hookstate/core';
import { Result } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate, Route, Routes } from 'react-router';
import authSlice from '../../utils/redux/slice/authSlice';
import { getRoutes } from '../app/appRoutes';
import AppLayout from '../app/layout/AppLayout';
import LoginScreen from '../auth/LoginScreen';
import RegisterScreen from '../auth/RegisterScreen';
import VerifyEmailScreen from '../auth/VerifyEmailScreen';
import ForgotPasswordScreen from '../auth/ForgotPasswordScreen';
import ResetPasswordScreen from '../auth/ResetPasswordScreen';
import SplashScreen from '../splash/SplashScreen';
import InvitationScreen from '../app/accountScreen/InvitationScreen';
import LandingPage from '../landing/index';
import TermsScreen from '../termsScreen/TermsScreen';
import PrivacyScreen from '../privacyScreen/PrivacyScreen';
import { peekConnectorIntent } from '../../connectorIntent';
import ConnectScreen from '../connect/ConnectScreen';

const HydrateFallback = () => {
  return (
    <div>
      <div className='flex items-center justify-center h-screen'>
        <div className='flex flex-col items-center'>
          <div className='text-2xl font-bold text-gray-800'>Loading...</div>
        </div>
      </div>
    </div>
  );
};

const ErrorBoundary = ({ error }) => {
  return (
    <div>
      <div className='flex items-center justify-center h-screen'>
        <div className='flex flex-col items-center'>
          <div className='text-2xl font-bold text-gray-800'>Error</div>
          <div className='text-gray-800'>{error.message}</div>
        </div>
      </div>
    </div>
  );
};

function Navigation(
  { appState } = {
    appState: hookstate({
      loading: true,
      error: null,
    }),
  }
) {
  const authenticated = useSelector(authSlice.selectors.isAuthenticated);
  const connectorIntent = peekConnectorIntent();
  const authenticatedDestination = connectorIntent ? '/account' : '/explore';

  useEffect(() => {}, [authenticated]);
  const filteredRoutes = getRoutes(); // Get routes user has access to

  // Splash stays mounted while an error is set too — otherwise its
  // connection-problem/retry screen unmounts before it can ever render.
  // NOTE: hookstate returns undefined (not a State) for a null-valued child,
  // so the error read must be optional-chained.
  if (appState.loading.get() === true || appState.error?.get?.()) {
    return <SplashScreen state={appState} />;
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/login' element={<LoginScreen />} />
        <Route path='/register' element={<RegisterScreen />} />
        <Route path='/verify-email' element={<VerifyEmailScreen />} />
        <Route path='/forgot-password' element={<ForgotPasswordScreen />} />
        <Route path='/reset-password' element={<ResetPasswordScreen />} />
        <Route path='/invitation' element={<InvitationScreen />} />
        <Route path='/terms' element={<TermsScreen />} />
        <Route path='/privacy' element={<PrivacyScreen />} />
        <Route path='/connect' element={<ConnectScreen />} />
        <Route path='*' element={<Navigate replace to={connectorIntent ? '/login' : '/'} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path='/connect' element={<ConnectScreen authenticated />} />
      {/* Single responsive layout - shows sidebar on desktop, bottom nav on mobile */}
      <Route
        element={<AppLayout />}
        fallback={<HydrateFallback />}
        errorBoundary={<ErrorBoundary />}
        hydrateFallbackElement={<HydrateFallback />}
      >
        {filteredRoutes.map((route) => (
          <Route key={route.key} path={route.path} element={route.component} />
        ))}
        <Route
          path='/404'
          element={
            <Result
              status='404'
              title='404'
              subTitle='Sorry, the page you visited does not exist.'
              extra={<Link to='/'>Back Home</Link>}
            />
          }
        />
        {/* Public pages accessible while logged in */}
        <Route path='/terms' element={<TermsScreen />} />
        <Route path='/privacy' element={<PrivacyScreen />} />
        {/* Redirect auth pages to app when already logged in */}
        <Route path='/login' element={<Navigate replace to={authenticatedDestination} />} />
        <Route path='/register' element={<Navigate replace to={authenticatedDestination} />} />
        <Route
          path='/'
          element={<Navigate replace to={authenticatedDestination} />}
        />
        {/* Redirect old /m routes to new routes */}
        <Route path='/m/explore' element={<Navigate replace to='/explore' />} />
        <Route path='/m/community' element={<Navigate replace to='/community' />} />
        <Route path='/m/gallery' element={<Navigate replace to='/gallery' />} />
        <Route path='/m/profile' element={<Navigate replace to='/account' />} />
        <Route path='/m/create/:id' element={<Navigate replace to='/create/:id' />} />
        <Route path='*' element={<Navigate replace to='/404' />} />
      </Route>
    </Routes>
  );
}

export default Navigation;
